"use client";

import { TYPE_META } from "@/icons/DataTypeIcon";
import { cn } from "@/lib/utils";
import { NoxusJsonView } from "@/components/UI/NoxusJsonView";
import type { DataType } from "@/generated";
import { CompactListPreview } from "@/components/Spotflow/WorkflowRunner/Outputs/ioListPreview";
import { IoEmptyValue } from "@/components/Spotflow/WorkflowRunner/Outputs/ioEmptyValue";
import {
  coerceIoFileLike,
  coerceIoTextContainer,
  getIoFileHref,
  IoFileLink,
} from "@/components/Spotflow/WorkflowRunner/Outputs/ioFileDisplay";
import { SpotMarkdown } from "@/components/Spotflow/SpotMarkdown/SpotMarkdown";
import {
  SkippedBadge,
  isSkippedValue,
} from "@/components/Spotflow/WorkflowRunner/Outputs/skippedDisplay";
import { Download } from "lucide-react";
import Link from "next/link";

/** The text a str-typed IO value would render as markdown, or null when the
 *  value isn't a renderable string (other types, lists, empty text). Shared
 *  with the row components so their "show raw" toggle matches what renders. */
export function ioMarkdownText(
  value: unknown,
  dataType: DataType | string
): string | null {
  if (dataType !== "str" && dataType !== "string") return null;
  const container = coerceIoTextContainer(value);
  let text: string | null = null;
  if (container) {
    text = container.text;
  } else if (typeof value === "string") {
    text = value;
  } else if (
    typeof value === "object" &&
    value !== null &&
    "text" in value &&
    typeof (value as { text: unknown }).text === "string"
  ) {
    text = (value as { text: string }).text;
  }
  return text !== null && text.trim() !== "" ? text : null;
}

/**
 * Type-aware value renderer used in the run-progress Inputs (bindings list)
 * and Results tabs. Renders the value with formatting matching its declared
 * `dataType` + an optional small type chip on the right.
 *
 * Falls back to JSON for unknown shapes so we never blank-screen on a value
 * the engine produced.
 */
export function TypedValueBadge({
  value,
  dataType,
  isList,
  showTypeChip = true,
  wrapContent = false,
  showAllListItems = false,
  markdown = false,
  rawListItems = false,
  className,
}: {
  value: unknown;
  dataType: DataType | string;
  isList?: boolean;
  showTypeChip?: boolean;
  wrapContent?: boolean;
  showAllListItems?: boolean;
  // Render str values as markdown instead of pre-wrapped plain text. Callers
  // pair it with a "show raw" toggle.
  markdown?: boolean;
  rawListItems?: boolean;
  className?: string;
}) {
  const meta = TYPE_META[dataType];

  return (
    <div
      className={cn(
        wrapContent
          ? "block w-full min-w-0 max-w-full"
          : "inline-flex items-center gap-2 min-w-0 max-w-full",
        className
      )}
    >
      <span
        className={cn(
          "block min-w-0 w-full text-sm",
          wrapContent || !showTypeChip
            ? "break-words whitespace-pre-wrap"
            : "truncate"
        )}
      >
        <ValueText
          value={value}
          dataType={dataType}
          isList={!!isList}
          wrap={wrapContent || !showTypeChip}
          showAllListItems={showAllListItems}
          markdown={markdown}
          rawListItems={rawListItems}
        />
      </span>
      {showTypeChip && meta && (
        <span className="shrink-0 inline-flex items-center gap-1 rounded-sm border border-border bg-muted px-1.5 py-px text-[10px] font-medium text-muted-foreground">
          <meta.icon size={10} strokeWidth={1.75} />
          {isList ? `List of ${meta.label}` : meta.label}
        </span>
      )}
    </div>
  );
}

function ValueText({
  value,
  dataType,
  isList,
  wrap,
  showAllListItems,
  markdown,
  rawListItems,
}: {
  value: unknown;
  dataType: string;
  isList: boolean;
  wrap: boolean;
  showAllListItems: boolean;
  markdown: boolean;
  rawListItems: boolean;
}) {
  const textClass = wrap ? "break-words whitespace-pre-wrap" : "truncate";
  if (value === null || value === undefined) {
    return <IoEmptyValue density="comfortable" />;
  }
  if (typeof value === "string" && value.trim() === "") {
    return <IoEmptyValue density="comfortable" />;
  }
  if (isSkippedValue(value)) {
    return <SkippedBadge />;
  }
  if (isList && Array.isArray(value)) {
    return (
      <CompactListPreview
        items={value}
        dataType={dataType}
        maxVisible={showAllListItems ? undefined : 2}
        density={showAllListItems ? "comfortable" : "compact"}
        rawItems={rawListItems}
      />
    );
  }

  const comfortable = showAllListItems;
  const markdownText = markdown ? ioMarkdownText(value, dataType) : null;

  const textContainer = coerceIoTextContainer(value);
  if (textContainer) {
    if (textContainer.text.trim() === "" && !textContainer.file) {
      return <IoEmptyValue density="comfortable" />;
    }
    return (
      <div className="rounded-md border border-border/40 bg-muted/15 px-2.5 py-2">
        {markdownText !== null ? (
          <SpotMarkdown
            markdown={markdownText}
            options={{ breaks: true, hideCodeTitle: true }}
          />
        ) : (
          <p
            className={cn(
              comfortable
                ? "text-sm text-foreground leading-relaxed break-words whitespace-pre-wrap"
                : "text-[10px] text-foreground/80 break-words whitespace-pre-wrap",
              !wrap && !comfortable && "line-clamp-4"
            )}
          >
            {textContainer.text}
          </p>
        )}
        {textContainer.has_preview && (
          <div
            className={cn(
              "mt-1 flex items-center gap-1 text-muted-foreground/60",
              comfortable ? "text-xs" : "text-[9px]"
            )}
          >
            <span className="italic">Truncated due to output size</span>
            {textContainer.file && (
              <Link
                href={getIoFileHref(textContainer.file, false)}
                download
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-0.5 not-italic text-primary hover:underline"
              >
                <Download className={comfortable ? "size-3" : "size-2.5"} />
                Download
              </Link>
            )}
          </div>
        )}
      </div>
    );
  }

  const file = coerceIoFileLike(value);
  if (file) {
    return (
      <IoFileLink
        value={file}
        className={
          comfortable
            ? "text-sm text-foreground hover:underline break-words leading-relaxed"
            : "text-[10px] text-foreground/85 hover:text-foreground hover:underline break-words"
        }
      />
    );
  }

  switch (dataType) {
    case "bool":
      return (
        <span className="font-medium tabular-nums">
          {value === true || value === "true" ? "True" : "False"}
        </span>
      );
    case "number":
      return (
        <span className="tabular-nums">
          {typeof value === "number" ? value.toLocaleString() : String(value)}
        </span>
      );
    case "datetime":
      return <span className="tabular-nums">{formatDateMaybe(value)}</span>;
    case "File":
    case "Image":
    case "Audio": {
      if (typeof value === "object" && value !== null) {
        const f = value as { name?: string; uri?: string };
        return <span className={textClass}>{f.name ?? f.uri ?? "(file)"}</span>;
      }
      return <span className={textClass}>{String(value)}</span>;
    }
    case "object":
    case "dict": {
      if (comfortable && typeof value === "object" && value !== null) {
        return (
          <div className="min-w-0 max-w-full overflow-x-auto">
            <NoxusJsonView data={value as object} expand={2} />
          </div>
        );
      }
      let content = "";
      try {
        content = JSON.stringify(value);
      } catch {
        content = String(value);
      }
      return (
        <span
          className={cn("font-mono text-xs text-muted-foreground", textClass)}
        >
          {content}
        </span>
      );
    }
    case "str":
    default: {
      if (markdownText !== null) {
        return (
          <SpotMarkdown
            markdown={markdownText}
            options={{ breaks: true, hideCodeTitle: true }}
          />
        );
      }
      // `TextContainer` shape ({ text, has_preview, file }).
      if (
        typeof value === "object" &&
        value !== null &&
        "text" in (value as Record<string, unknown>)
      ) {
        const t = (value as { text?: unknown }).text;
        if (typeof t === "string")
          return <span className={textClass}>{t}</span>;
      }
      if (typeof value === "string")
        return <span className={textClass}>{value}</span>;
      let content = "";
      try {
        content = JSON.stringify(value);
      } catch {
        content = String(value);
      }

      return (
        <span
          className={cn("font-mono text-xs text-muted-foreground", textClass)}
        >
          {content}
        </span>
      );
    }
  }
}

function formatDateMaybe(value: unknown): string {
  if (typeof value !== "string" && typeof value !== "number")
    return String(value);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}
